import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Search, 
  Bell, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Brain,
  Target,
  TrendingUp,
  Shield,
  Zap,
  Users,
  BarChart3,
  FileText,
  Settings
} from 'lucide-react';

export default function PlateformeAppelsOffresPage() {
  return (
    <>
      <SEO 
        title="Plateforme Appels d'Offres | Veille et Gestion Marchés Publics"
        description="Découvrez la plateforme de référence pour les appels d'offres. Veille automatisée, analyse IA des DCE, suivi des candidatures. Essai gratuit."
        path="/plateforme-appels-offres"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <Breadcrumb items={[{ label: "Plateforme appels d'offres", href: "/plateforme-appels-offres" }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
                <Settings className="w-4 h-4" />
                <span>Solution tout-en-un</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                La plateforme d'appels d'offres
                <br />
                <span className="text-violet-300">qui simplifie votre quotidien</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Gérez l'intégralité de votre processus de réponse aux marchés publics : veille, analyse, rédaction et suivi. Une seule plateforme, des résultats concrets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                    Essayer gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Qu'est-ce qu'un logiciel d'appels d'offres */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Qu'est-ce qu'un logiciel de gestion des appels d'offres ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Un <strong>logiciel d'appels d'offres</strong> est un outil professionnel conçu pour accompagner les entreprises dans leur stratégie de réponse aux marchés publics. Il centralise et automatise les différentes étapes du processus : de la détection des opportunités jusqu'au suivi des candidatures.
                </p>
                <p>
                  Face à la complexité croissante de la commande publique et au volume important d'appels d'offres publiés chaque jour (plus de <strong>500 nouveaux marchés quotidiens</strong> en France), disposer d'un outil performant est devenu indispensable pour rester compétitif.
                </p>
                <p>
                  Les entreprises qui utilisent un logiciel dédié augmentent significativement leur <strong>taux de réussite</strong> aux appels d'offres. Pourquoi ? Parce qu'elles identifient plus rapidement les opportunités, analysent plus efficacement les dossiers, et structurent mieux leurs réponses.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Fonctionnalités clés */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les fonctionnalités essentielles d'un bon logiciel
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Bell,
                  title: "Veille automatisée",
                  desc: "Surveillance en continu des sources officielles (BOAMP, DECP, TED) avec alertes personnalisées selon vos critères métier."
                },
                {
                  icon: Brain,
                  title: "Analyse IA des DCE",
                  desc: "Extraction automatique des informations clés des dossiers de consultation : critères, délais, pénalités, exigences techniques."
                },
                {
                  icon: Target,
                  title: "Scoring de pertinence",
                  desc: "Évaluation automatique de chaque marché selon votre profil pour prioriser les opportunités les plus prometteuses."
                },
                {
                  icon: FileText,
                  title: "Bibliothèque de réponses",
                  desc: "Centralisez vos références, certifications et contenus réutilisables pour gagner du temps sur chaque candidature."
                },
                {
                  icon: BarChart3,
                  title: "Tableau de bord analytique",
                  desc: "Suivez vos KPIs : taux de réussite, marchés gagnés, montants, évolution de votre performance commerciale."
                },
                {
                  icon: Users,
                  title: "Travail collaboratif",
                  desc: "Partagez les dossiers avec vos équipes, assignez des tâches, suivez l'avancement de chaque réponse."
                }
              ].map((feature, i) => (
                <Card key={`item-${i}`} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Pourquoi DCE Express */}
        <section className="py-16 bg-violet-900 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Pourquoi choisir DCE Express ?
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg prose-invert max-w-none">
                <p>
                  Parmi les nombreux logiciels de gestion des appels d'offres disponibles sur le marché, <strong>DCE Express</strong> se distingue par son approche centrée sur l'intelligence artificielle et l'expérience utilisateur.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                {[
                  {
                    title: "Intelligence artificielle avancée",
                    desc: "Notre IA analyse en profondeur les documents de consultation pour en extraire la substantifique moelle : critères d'attribution, points de vigilance, opportunités cachées."
                  },
                  {
                    title: "Interface intuitive",
                    desc: "Pas besoin de formation complexe. Notre interface a été pensée pour être immédiatement opérationnelle, même pour les non-initiés."
                  },
                  {
                    title: "Tarification accessible",
                    desc: "Contrairement aux solutions traditionnelles facturées plusieurs centaines d'euros par mois, DCE Express propose des offres adaptées aux TPE/PME."
                  },
                  {
                    title: "Support réactif",
                    desc: "Notre équipe d'experts en marchés publics vous accompagne dans la prise en main et répond à vos questions métier."
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-violet-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                      <p className="text-slate-300">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comparatif */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              Comment choisir son logiciel d'appels d'offres ?
            </h2>
            <div className="max-w-4xl mx-auto prose prose-lg text-slate-600">
              <p>
                Le choix d'un logiciel de gestion des appels d'offres dépend de plusieurs critères que vous devez évaluer selon votre contexte :
              </p>
              <h3>1. Vos besoins réels</h3>
              <p>
                Avez-vous besoin d'un simple outil de veille ou d'une solution complète intégrant l'analyse des DCE et le suivi des candidatures ? Répondez-vous à quelques marchés par an ou à plusieurs dizaines ?
              </p>
              <h3>2. Votre budget</h3>
              <p>
                Les tarifs varient considérablement : de quelques dizaines d'euros à plusieurs milliers par mois. Les solutions les plus chères ne sont pas forcément les mieux adaptées à votre situation.
              </p>
              <h3>3. La qualité des sources</h3>
              <p>
                Vérifiez que le logiciel surveille bien les sources pertinentes pour votre activité : BOAMP, JOUE/TED, plateformes sectorielles, donneurs d'ordres spécifiques...
              </p>
              <h3>4. L'ergonomie</h3>
              <p>
                Un logiciel performant mais complexe à utiliser sera sous-exploité. Privilégiez les solutions offrant une période d'essai pour tester l'interface.
              </p>
              <h3>5. L'accompagnement</h3>
              <p>
                Le support client et la documentation disponible sont essentiels, surtout si vous débutez dans les marchés publics.
              </p>
            </div>
          </div>
        </section>

        {/* Témoignages */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Ce que nos utilisateurs en disent
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  quote: "Depuis que j'utilise DCE Express, j'ai triplé mon nombre de réponses aux appels d'offres tout en y passant moins de temps.",
                  author: "Marie D.",
                  role: "Dirigeante PME BTP"
                },
                {
                  quote: "L'analyse IA des DCE est bluffante. En 30 secondes, j'ai une synthèse complète du cahier des charges.",
                  author: "Thomas L.",
                  role: "Responsable commercial"
                },
                {
                  quote: "Enfin un outil adapté aux petites structures avec un prix raisonnable et des fonctionnalités de grande qualité.",
                  author: "Sophie M.",
                  role: "Auto-entrepreneuse"
                }
              ].map((testimonial, i) => (
                <Card key={`item-${i}`} className="border-slate-200">
                  <CardContent className="p-6">
                    <p className="text-slate-600 italic mb-4">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.author}</p>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à optimiser vos réponses aux appels d'offres ?
            </h2>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
              Testez DCE Express gratuitement pendant 14 jours. Sans engagement, sans carte bancaire.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                Démarrer mon essai gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="plateforme" />

        <Footer />
      </div>
    </>
  );
}
